import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { BACKEND_URL, fileUrl } from "../lib/api";
import { PlatformChip } from "../lib/meta";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";

export default function ClientPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [comments, setComments] = useState({});
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    axios.get(`${BACKEND_URL}/api/public/portal/${token}`).then((r) => setData(r.data)).catch(() => setError(true));
  }, [token]);
  useEffect(load, [load]);

  const act = async (draftId, action) => {
    setBusy(draftId + action);
    try {
      await axios.post(`${BACKEND_URL}/api/public/portal/${token}/drafts/${draftId}/${action}`, { comment: comments[draftId] || "" });
      toast.success(action === "approve" ? "Approved — thank you!" : "Change request sent to the team");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally { setBusy(""); }
  };

  if (error) return <div className="min-h-screen flex items-center justify-center text-sm text-slate-400">This review portal isn't available. Ask your agency for a fresh link.</div>;
  if (!data) return null;

  return (
    <div className="min-h-screen bg-[hsl(30,33%,98%)]" data-testid="client-portal-page">
      <header className="bg-white border-b border-slate-200 px-6 py-5">
        <div className="max-w-xl mx-auto flex items-center gap-3">
          <span className="w-9 h-9 rounded-xl" style={{ background: data.color }} />
          <div>
            <h1 className="font-display font-bold text-lg tracking-tight leading-none">{data.client_name} — Content Review</h1>
            <p className="text-[11px] text-slate-400 mt-0.5">Prepared by {data.org_name}</p>
          </div>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-4 py-8 space-y-4">
        {data.drafts.length === 0 && (
          <p className="text-center text-sm text-slate-400 py-14 bg-white border border-slate-200 rounded-2xl" data-testid="portal-empty">All caught up — nothing waiting for your review. 🎉</p>
        )}
        {data.drafts.map((d) => (
          <div key={d.draft_id} data-testid={`portal-draft-${d.draft_id}`} className="bg-white border border-slate-200 rounded-2xl p-5 card-float">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <PlatformChip platform={d.platform} />
                {d.ai_generated && <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">AI-assisted</span>}
              </div>
              {d.scheduled_date && <span className="text-[11px] text-slate-400">{d.scheduled_date}</span>}
            </div>
            {d.image_url && <img src={fileUrl(d.image_url)} alt="" className="mt-3 rounded-xl border border-slate-100 w-full max-h-72 object-cover" />}
            <p className="mt-3 text-[13px] text-slate-700 whitespace-pre-wrap leading-relaxed">{d.caption}</p>
            <Textarea data-testid={`portal-comment-${d.draft_id}`} rows={2} className="mt-4 text-[13px]"
              placeholder="Optional note (required if requesting changes)"
              value={comments[d.draft_id] || ""} onChange={(e) => setComments({ ...comments, [d.draft_id]: e.target.value })} />
            <div className="mt-3 grid grid-cols-2 gap-2">
              <Button data-testid={`portal-changes-${d.draft_id}`} variant="outline" disabled={busy === d.draft_id + "request-changes"}
                className="rounded-xl text-orange-600 border-orange-200 hover:bg-orange-50" onClick={() => act(d.draft_id, "request-changes")}>
                Request changes
              </Button>
              <Button data-testid={`portal-approve-${d.draft_id}`} disabled={busy === d.draft_id + "approve"}
                className="rounded-xl bg-emerald-600 hover:bg-emerald-500" onClick={() => act(d.draft_id, "approve")}>
                Approve
              </Button>
            </div>
          </div>
        ))}
        <p className="text-center text-[11px] text-slate-300 pt-4">Secure review link · no account needed · powered by {data.org_name}</p>
      </main>
    </div>
  );
}
